import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { AuthLayout } from './AuthLayout';
import {
  AppleLogo,
  MAC_URL,
  thisComputer,
  UBUNTU_URL,
  UbuntuLogo,
  WINDOWS_URL,
  WindowsLogo,
} from './DesktopDownloads';
import { authButtonSx } from './styles';

type Os = 'windows' | 'mac' | 'ubuntu';

/** A terminal command with a copy button. */
function Command({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 1,
        pl: 1.5,
        pr: 0.5,
        py: 0.5,
        borderRadius: '10px',
        bgcolor: '#0F1F1C',
        color: '#E6F4F1',
        border: `1px solid ${alpha(t.palette.common.white, 0.08)}`,
      })}
    >
      <Box
        component="code"
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 12.5,
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          py: 0.75,
        }}
      >
        {children}
      </Box>
      <Tooltip title={copied ? 'Copied' : 'Copy'}>
        <IconButton
          size="small"
          aria-label="Copy command"
          onClick={() => {
            void navigator.clipboard.writeText(children).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
          sx={{ color: '#9ED9CC' }}
        >
          {copied ? <CheckRounded fontSize="small" /> : <ContentCopyRounded fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/** Numbered installation steps. */
function Steps({ items }: { items: ReactNode[] }) {
  return (
    <Box
      component="ol"
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: 1.75,
      }}
    >
      {items.map((item, i) => (
        <Box component="li" key={i} sx={{ display: 'flex', gap: 1.5 }}>
          <Box
            sx={{
              flexShrink: 0,
              width: 26,
              height: 26,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              fontWeight: 700,
              color: 'primary.main',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
            }}
          >
            {i + 1}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1, fontSize: 14, lineHeight: 1.55, pt: 0.25 }}>{item}</Box>
        </Box>
      ))}
    </Box>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 650, fontSize: 14, mb: 1 }}>{title}</Typography>
      <Box sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.55 }}>{children}</Box>
    </Box>
  );
}

const b = (text: string) => <b>{text}</b>;

const GUIDES: Record<
  Os,
  {
    label: string;
    logo: ReactNode;
    href: string;
    file: string;
    needs: string;
    steps: ReactNode[];
    update: ReactNode;
    remove: ReactNode;
  }
> = {
  windows: {
    label: 'Windows',
    logo: <WindowsLogo />,
    href: WINDOWS_URL,
    file: 'PackPilot-Setup.exe',
    needs: 'Windows 10 or 11, 64-bit. No administrator rights needed.',
    steps: [
      <>Download {b('PackPilot-Setup.exe')} and open it from your Downloads folder.</>,
      <>
        If Windows shows “Windows protected your PC”, click {b('More info')}, then {b('Run anyway')}
        .
      </>,
      <>Choose where to install (the suggested folder is fine) and click {b('Install')}.</>,
      <>
        Open {b('PackPilot')} from the Start menu or the desktop shortcut, and sign in with your
        shop ID.
      </>,
    ],
    update: <>Download the new installer and run it over the old one. Your data stays.</>,
    remove: (
      <>
        Settings → Apps → {b('PackPilot')} → Uninstall. Your shop data in{' '}
        <code>%APPDATA%\PackPilot</code> is kept, in case it holds sales not yet synced.
      </>
    ),
  },
  mac: {
    label: 'macOS',
    logo: <AppleLogo />,
    href: MAC_URL,
    file: 'PackPilot.dmg',
    needs: 'macOS 12 or newer, Intel or Apple silicon (one download for both).',
    steps: [
      <>Download {b('PackPilot.dmg')} and open it.</>,
      <>
        Drag {b('PackPilot')} onto the {b('Applications')} folder in the window that opens.
      </>,
      <>
        The first time only: in Applications, {b('Control-click')} (or right-click) PackPilot,
        choose {b('Open')}, then {b('Open')} again.
      </>,
      <>
        If macOS still refuses: System Settings → Privacy &amp; Security → {b('Open Anyway')}. Or
        run this in Terminal:
        <Command>xattr -dr com.apple.quarantine /Applications/PackPilot.app</Command>
      </>,
      <>Sign in with your shop ID. After that, PackPilot opens normally.</>,
    ],
    update: <>Open the new .dmg and drag PackPilot to Applications again (choose Replace).</>,
    remove: (
      <>
        Drag PackPilot from Applications to the Bin. Your shop data in{' '}
        <code>~/Library/Application Support/PackPilot</code> is kept.
      </>
    ),
  },
  ubuntu: {
    label: 'Ubuntu',
    logo: <UbuntuLogo />,
    href: UBUNTU_URL,
    file: 'packpilot_amd64.deb',
    needs: 'Ubuntu 24.04 or newer (or Debian), 64-bit.',
    steps: [
      <>Download {b('packpilot_amd64.deb')} (it goes to your Downloads folder).</>,
      <>
        Open a terminal (Ctrl + Alt + T) and install it:
        <Command>sudo apt install ~/Downloads/packpilot_amd64.deb</Command>
      </>,
      <>
        Open {b('PackPilot')} from the app menu, or type <code>packpilot</code> in the terminal, and
        sign in with your shop ID.
      </>,
    ],
    update: (
      <>
        Download the new .deb and run the same command. Close PackPilot first.
        <Command>sudo apt install ~/Downloads/packpilot_amd64.deb</Command>
      </>
    ),
    remove: (
      <>
        <Command>sudo apt remove packpilot</Command>
        <Box sx={{ mt: 1 }}>
          Your shop data in <code>~/.config/PackPilot</code> is kept.
        </Box>
      </>
    ),
  },
};

const isOs = (v: string | null): v is Os => v === 'windows' || v === 'mac' || v === 'ubuntu';

/**
 * Public page: how to install the desktop app on each system (linked from the sign-in page).
 * Opens on this computer's system; `/install?os=mac` (windows, mac, ubuntu) opens a given tab.
 */
export function InstallPage() {
  const [params, setParams] = useSearchParams();
  const asked = params.get('os');
  const os: Os = isOs(asked) ? asked : (thisComputer() ?? 'windows');
  const setOs = (v: Os) => setParams({ os: v }, { replace: true });
  const guide = GUIDES[os];
  return (
    <AuthLayout
      title="Install the desktop app"
      subtitle="PackPilot on your computer keeps selling when the internet drops, and syncs when it’s back."
      back={
        <Button
          component={RouterLink}
          to="/login"
          variant="text"
          size="small"
          startIcon={<ArrowBackRounded />}
          sx={{ color: 'text.secondary', fontWeight: 600 }}
        >
          Back to sign in
        </Button>
      }
    >
      <Stack spacing={3}>
        <Tabs
          value={os}
          onChange={(_, v: Os) => setOs(v)}
          variant="fullWidth"
          aria-label="Your computer"
          sx={{
            minHeight: 44,
            p: 0.5,
            borderRadius: '12px',
            bgcolor: (t) => alpha(t.palette.text.primary, 0.05),
            '& .MuiTabs-indicator': {
              height: '100%',
              borderRadius: '9px',
              zIndex: 0,
              bgcolor: 'background.paper',
              boxShadow: '0 1px 3px rgba(16,24,40,0.12)',
            },
            '& .MuiTab-root': {
              zIndex: 1,
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
            },
          }}
        >
          {(Object.keys(GUIDES) as Os[]).map((key) => (
            <Tab
              key={key}
              value={key}
              label={GUIDES[key].label}
              icon={<Box sx={{ display: 'grid' }}>{GUIDES[key].logo}</Box>}
              iconPosition="start"
            />
          ))}
        </Tabs>

        <Box>
          <Button
            component="a"
            href={guide.href}
            download={guide.file}
            fullWidth
            variant="contained"
            size="large"
            startIcon={<DownloadRounded />}
            sx={authButtonSx}
          >
            Download for {guide.label}
          </Button>
          <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 1, textAlign: 'center' }}>
            {guide.file} · {guide.needs}
          </Typography>
        </Box>

        <Section title="Install">
          <Box sx={{ color: 'text.primary' }}>
            <Steps items={guide.steps} />
          </Box>
        </Section>

        <Section title="Update to a new version">{guide.update}</Section>
        <Section title="Uninstall">{guide.remove}</Section>

        <Alert severity="info" sx={{ borderRadius: '12px' }}>
          Sign in once while online. After that the app works offline and sends your sales when the
          internet is back.
        </Alert>
      </Stack>
    </AuthLayout>
  );
}
